import { InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { ItemVM } from 'shell/view-models/item';
import { HttpResponseList } from 'shell/models/http-response';
import { Service } from 'shell/models/service';
import { Operation } from 'shell/view-models/operation';
import { PmiFormControl } from '../pmi-select/pmi-form-control';
import { PmiSelect } from '../pmi-select';
import { OperationObject } from 'system/utils/operation-object-to-query-string';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';

interface ListServiceProps {
  disabled?: boolean;
  value: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  label?: string;
  errorSelect?: boolean;
  fgCashManagement?: string;
  codService?: string;
}

const ListServiceComponent = ({
  disabled,
  onChange,
  value,
  label = 'Servizio',
  errorSelect = false,
  fgCashManagement,
  codService,
}: ListServiceProps): React.ReactElement => {
  const [servicesData, getServices] = useHttpFetch<HttpResponseList<Service>, Array<ItemVM>>(
    `${__MANAGEMENT_API_URL__}/servizi`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) =>
        ((response.data.payload.list || []) as Array<Service>).map((service) => ({
          id: service.key.codiceServizio,
          value: service.descrizione,
        })) as Array<ItemVM>,
    }
  );

  const services: Array<ItemVM> = useMemo(() => {
    return (servicesData.data || []) as Array<ItemVM>;
  }, [servicesData]);

  useEffect(() => {
    if (fgCashManagement || codService) {
      const transformedFilters: OperationObject = {
        gestioneCassa: {
          value: fgCashManagement || '',
          operation: Operation.EQUALS,
        },
        codiceServizio: {
          value: codService || '',
          operation: Operation.LIKE,
        },
      };

      const queryString = `${paginationListToQueryString({
        filters: transformedFilters,
        orders: {},
      })}`;

      getServices({ params: {}, paramsSerializer: () => queryString });
    } else {
      getServices();
    }
  }, []);

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={errorSelect} id={`service-${label}`}>
        {label}
      </InputLabel>
      <PmiSelect
        labelId={`service-${label}`}
        id="service"
        aria-label={label}
        name="service"
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled}
        error={errorSelect}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {services
          .sort((a, b) => a.value?.localeCompare(b.value))
          .map((service: ItemVM) => (
            <MenuItem key={service.id} value={service.id}>
              {service.value}
            </MenuItem>
          ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListServiceComponent.displayName = 'List Service component';

const ListService = memo(ListServiceComponent);

export default ListService as typeof ListServiceComponent;
